import React, { useState, useMemo, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Download, Calendar, Upload, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as pdfjsLib from 'pdfjs-dist';
import { toast } from 'sonner';

// Configurar worker de pdfjs - usar CDN confiable de unpkg
if (typeof window !== 'undefined') {
  try {
    // Intentar usar unpkg primero
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
    console.log('PDF.js worker configurado:', pdfjsLib.GlobalWorkerOptions.workerSrc);
  } catch (error) {
    console.error('Error configurando PDF.js worker:', error);
  }
}

// Función para formatear fecha en formato DD-MM-YYYY
function formatGTDate(dateStr: string) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

interface MovimientoBanco {
  fecha: string;
  documento: string; // Información descriptiva formateada (Boleta, Recibo, etc.)
  transferencia?: string; // DEPRECATED: Usar noAutorizacion y noDoc
  noAutorizacion?: string; // No. Autorización (número de autorización/transferencia)
  noDoc?: string; // No. Doc. del banco (para match)
  descripcion: string;
  debito: number;
  credito: number;
  saldo: number;
}

interface ComparacionResultado {
  movimientosSistema: MovimientoBanco[];
  movimientosBanco: MovimientoBanco[];
  faltantesEnSistema: MovimientoBanco[];
  faltantesEnBanco: MovimientoBanco[];
  coincidencias: Array<{ sistema: MovimientoBanco; banco: MovimientoBanco }>;
  diferencias: Array<{ sistema: MovimientoBanco; banco: MovimientoBanco; motivo: string }>;
}

export function EstadoCuentaBancario() {
  const { expenses, config } = useAppData();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [comparacion, setComparacion] = useState<ComparacionResultado | null>(null);
  const [procesandoPDF, setProcesandoPDF] = useState(false);
  const [estadoProceso, setEstadoProceso] = useState<string>('');
  const [mesComparacion, setMesComparacion] = useState<number | null>(null);
  const [anioComparacion, setAnioComparacion] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Calcular movimientos del mes seleccionado
  const movimientosMes = useMemo(() => {
    return expenses
      .filter((mov: any) => {
        if (!mov.fecha) return false;
        const fechaMov = new Date(mov.fecha);
        return fechaMov.getMonth() === selectedMonth && fechaMov.getFullYear() === selectedYear;
      })
      .sort((a: any, b: any) => {
        const fechaA = new Date(a.fecha).getTime();
        const fechaB = new Date(b.fecha).getTime();
        return fechaA - fechaB;
      });
  }, [expenses, selectedMonth, selectedYear]);

  // Calcular saldo anterior (suma de todos los movimientos antes del mes seleccionado)
  const saldoAnterior = useMemo(() => {
    return expenses
      .filter((mov: any) => {
        if (!mov.fecha) return false;
        const fechaMov = new Date(mov.fecha);
        const fechaLimite = new Date(selectedYear, selectedMonth, 1);
        return fechaMov < fechaLimite;
      })
      .reduce((saldo: number, mov: any) => {
        const tipo = (mov.tipo || '').toLowerCase();
        const monto = Number(mov.monto) || 0;

        if (['ingreso', 'contado', 'enganche', 'abono'].includes(tipo)) {
          return saldo + monto; // Crédito
        } else if (['egreso', 'comision'].includes(tipo)) {
          return saldo - monto; // Débito
        }
        return saldo;
      }, 0);
  }, [expenses, selectedMonth, selectedYear]);

  // Función para extraer número de documento para match con banco
  // Prioridad: numeroDocumento > transferencia > parsear referencia > fallback
  const extraerTransferencia = (mov: any): string => {
    // 1. PRIORIDAD: Usar numeroDocumento (campo nuevo para match con banco)
    if (mov.numeroDocumento && mov.numeroDocumento.toString().trim() && mov.numeroDocumento.toString().trim() !== '-') {
      return mov.numeroDocumento.toString().trim();
    }

    // 2. Usar transferencia si existe (campo directo)
    if (mov.transferencia && mov.transferencia.toString().trim() && mov.transferencia.toString().trim() !== '-') {
      return mov.transferencia.toString().trim();
    }

    // 3. Buscar en referencia: "Transferencia: 123, Boleta: 456" o "No. Doc.: 123"
    if (mov.referencia) {
      // Buscar "No. Doc.:" primero (nuevo formato)
      const noDocMatch = mov.referencia.match(/No\.\s*Doc\.?:\s*([^,]+)/i);
      if (noDocMatch) {
        const noDoc = noDocMatch[1].trim();
        if (noDoc && noDoc !== '-') {
          return noDoc;
        }
      }

      // Buscar Transferencia en referencia
      const transferenciaMatch = mov.referencia.match(/Transferencia:\s*([^,]+)/i);
      if (transferenciaMatch) {
        const transferencia = transferenciaMatch[1].trim();
        if (transferencia && transferencia !== '-') {
          return transferencia;
        }
      }

      // Si no hay Transferencia, buscar Boleta
      if (mov.referencia.includes('Boleta:')) {
        const boletaMatch = mov.referencia.match(/Boleta:\s*([^,]+)/i);
        if (boletaMatch) {
          const boleta = boletaMatch[1].trim();
          if (boleta && boleta !== '-') {
            return boleta;
          }
        }
      }
    }

    // 4. Si es egreso manual sin transferencia, mostrar '-'
    if ((mov.tipo === 'egreso' || mov.tipo === 'comision') && !mov.automatic) {
      return '-';
    }

    // 5. Si no hay valor válido, mostrar "-" en lugar del ID de Firebase
    return '-';
  };

  // Función para formatear documento de manera legible
  const formatearDocumento = (mov: any): string => {
    // Si hay referencia, formatearla de manera legible
    if (mov.referencia) {
      const partes: string[] = [];

      // Extraer No. Doc. si existe
      const noDocMatch = mov.referencia.match(/No\.\s*Doc\.?:\s*([^,]+)/i);
      if (noDocMatch) {
        partes.push(`No. Doc.: ${noDocMatch[1].trim()}`);
      }

      // Extraer Transferencia
      const transferenciaMatch = mov.referencia.match(/Transferencia:\s*([^,]+)/i);
      if (transferenciaMatch) {
        partes.push(`Transferencia: ${transferenciaMatch[1].trim()}`);
      }

      // Extraer Boleta
      const boletaMatch = mov.referencia.match(/Boleta:\s*([^,]+)/i);
      if (boletaMatch) {
        partes.push(`Boleta: ${boletaMatch[1].trim()}`);
      }

      // Extraer Recibo
      const reciboMatch = mov.referencia.match(/Recibo:\s*([^,]+)/i);
      if (reciboMatch) {
        partes.push(`Recibo: ${reciboMatch[1].trim()}`);
      }

      // Extraer Cuota si existe
      const cuotaMatch = mov.referencia.match(/Cuota:\s*([^,]+)/i);
      if (cuotaMatch) {
        partes.push(`Cuota: ${cuotaMatch[1].trim()}`);
      }

      // Si encontramos partes formateadas, retornarlas
      if (partes.length > 0) {
        return partes.join(', ');
      }

      // Si no, retornar referencia completa
      return mov.referencia;
    }

    // Si hay numeroDocumento pero no referencia, mostrar eso
    if (mov.numeroDocumento) {
      return `No. Doc.: ${mov.numeroDocumento}`;
    }

    // Si es movimiento manual sin referencia, mostrar tipo
    if (!mov.automatic) {
      return mov.tipo === 'egreso' ? 'Egreso Manual' : 'Ingreso Manual';
    }

    // Si no hay información, mostrar '-'
    return '-';
  };

  // Función para extraer No. Autorización (número de autorización/transferencia)
  const extraerNoAutorizacion = (mov: any): string => {
    // 1. Usar transferencia si existe (campo directo)
    if (mov.transferencia && mov.transferencia.toString().trim() && mov.transferencia.toString().trim() !== '-') {
      return mov.transferencia.toString().trim();
    }

    // 2. Buscar en referencia: "No. Autorización: XXX" (formato nuevo)
    if (mov.referencia) {
      const noAutorizacionMatch = mov.referencia.match(/No\.\s*Autorizaci[oó]n:\s*([^,]+)/i);
      if (noAutorizacionMatch) {
        const noAutorizacion = noAutorizacionMatch[1].trim();
        if (noAutorizacion && noAutorizacion !== '-') {
          return noAutorizacion;
        }
      }

      // 3. Buscar "Transferencia: 123" (formato antiguo)
      const transferenciaMatch = mov.referencia.match(/Transferencia:\s*([^,]+)/i);
      if (transferenciaMatch) {
        const transferencia = transferenciaMatch[1].trim();
        if (transferencia && transferencia !== '-') {
          return transferencia;
        }
      }

      // 4. Si no hay Transferencia, buscar Boleta (muchos movimientos automáticos usan Boleta como autorización)
      const boletaMatch = mov.referencia.match(/Boleta:\s*([^,]+)/i);
      if (boletaMatch) {
        const boleta = boletaMatch[1].trim();
        if (boleta && boleta !== '-') {
          return boleta;
        }
      }

      // 5. Buscar Recibo como alternativa
      const reciboMatch = mov.referencia.match(/Recibo:\s*([^,]+)/i);
      if (reciboMatch) {
        const recibo = reciboMatch[1].trim();
        if (recibo && recibo !== '-') {
          return recibo;
        }
      }
    }

    // 6. Si no hay valor válido, mostrar "-" en lugar del ID de Firebase
    return '-';
  };

  // Calcular saldo acumulado y preparar datos para la tabla
  const datosTabla = useMemo(() => {
    let saldoAcumulado = saldoAnterior;
    const filas: Array<{
      dia: string;
      noAutorizacion: string;
      noDoc: string;
      documento: string;
      descripcion: string;
      debito: number;
      credito: number;
      saldo: number;
    }> = [];

    // Agrupar movimientos por numeroDocumento y fecha para consolidar
    // Solo agrupar si tienen numeroDocumento válido (no vacío ni '-')
    const grupos = new Map<string, any[]>();
    const movimientosNoAgrupables: any[] = [];

    movimientosMes.forEach((mov: any) => {
      const noDoc = (mov.numeroDocumento && mov.numeroDocumento.toString().trim() && mov.numeroDocumento.toString().trim() !== '-')
        ? mov.numeroDocumento.toString().trim()
        : (mov.referencia ? (() => {
          const noDocMatch = mov.referencia.match(/No\.\s*Doc\.?:\s*([^,]+)/i);
          return (noDocMatch && noDocMatch[1].trim() && noDocMatch[1].trim() !== '-') ? noDocMatch[1].trim() : null;
        })() : null);

      const fecha = new Date(mov.fecha);
      const fechaStr = fecha.toISOString().split('T')[0]; // YYYY-MM-DD

      // Solo agrupar si tiene numeroDocumento válido y es ingreso/abono
      const tipo = (mov.tipo || '').toLowerCase();
      const esIngreso = ['ingreso', 'contado', 'enganche', 'abono'].includes(tipo);

      if (noDoc && noDoc !== '-' && esIngreso) {
        const claveGrupo = `${noDoc}_${fechaStr}`;
        if (!grupos.has(claveGrupo)) {
          grupos.set(claveGrupo, []);
        }
        grupos.get(claveGrupo)!.push(mov);
      } else {
        // Movimientos que no se agrupan (sin numeroDocumento o egresos)
        movimientosNoAgrupables.push(mov);
      }
    });

    // Procesar grupos consolidados
    grupos.forEach((movimientosGrupo, claveGrupo) => {
      if (movimientosGrupo.length === 1) {
        // Solo un movimiento, procesar normalmente
        movimientosNoAgrupables.push(movimientosGrupo[0]);
      } else {
        // Múltiples movimientos con mismo numeroDocumento y fecha - consolidar
        const primerMov = movimientosGrupo[0];
        const fecha = new Date(primerMov.fecha);
        const dia = fecha.getDate().toString().padStart(2, '0');
        const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
        const anio = fecha.getFullYear();

        // Sumar montos
        const montoTotal = movimientosGrupo.reduce((sum, m) => sum + (Number(m.monto) || 0), 0);

        // Construir descripción consolidada con todos los lotes
        const lotesUnicos = new Set<string>();
        const compradoresUnicos = new Set<string>();
        const conceptos: string[] = [];

        movimientosGrupo.forEach((m: any) => {
          // Extraer lotes
          if (m.lotes) {
            const lotesArray = Array.isArray(m.lotes) ? m.lotes : [m.lotes];
            lotesArray.forEach((l: string) => lotesUnicos.add(l));
          }
          // Extraer comprador (directo o del concepto)
          if (m.comprador) {
            compradoresUnicos.add(m.comprador);
          } else if (m.concepto) {
            // Intentar extraer comprador del concepto: "Abono manual de NOMBRE para lote..."
            const matchComprador = m.concepto.match(/(?:Abono manual de|Abono de|Enganche por venta de lotes.*a)\s+([^para]+?)(?:\s+para|\s+a|$)/i);
            if (matchComprador) {
              compradoresUnicos.add(matchComprador[1].trim());
            }
          }
          // Extraer concepto para referencia
          if (m.concepto) {
            conceptos.push(m.concepto);
          }
        });

        // Construir descripción consolidada
        const lotesStr = Array.from(lotesUnicos).sort().join(', ');
        const compradoresStr = Array.from(compradoresUnicos).join(', ');

        let descripcionConsolidada = '';
        if (conceptos.some(c => c.includes('Abono manual'))) {
          // Es abono manual
          descripcionConsolidada = `Abono manual de ${compradoresStr || 'varios compradores'} para lotes ${lotesStr}`;
        } else {
          // Otro tipo de ingreso
          descripcionConsolidada = `Ingreso consolidado para lotes ${lotesStr}`;
          if (compradoresStr) {
            descripcionConsolidada += ` - ${compradoresStr}`;
          }
        }

        const noAutorizacion = extraerNoAutorizacion(primerMov);
        const noDoc = (primerMov.numeroDocumento && primerMov.numeroDocumento.toString().trim() && primerMov.numeroDocumento.toString().trim() !== '-')
          ? primerMov.numeroDocumento.toString().trim()
          : (primerMov.referencia ? (() => {
            const noDocMatch = primerMov.referencia.match(/No\.\s*Doc\.?:\s*([^,]+)/i);
            return (noDocMatch && noDocMatch[1].trim() && noDocMatch[1].trim() !== '-') ? noDocMatch[1].trim() : '-';
          })() : '-');
        const documento = formatearDocumento(primerMov);

        // Agregar como movimiento consolidado
        filas.push({
          dia: `${dia}-${mes}-${anio}`,
          noAutorizacion,
          noDoc,
          documento,
          descripcion: descripcionConsolidada,
          debito: 0,
          credito: montoTotal,
          saldo: saldoAcumulado + montoTotal
        });

        saldoAcumulado += montoTotal;
      }
    });

    // Procesar movimientos no agrupables (sin numeroDocumento o egresos)
    movimientosNoAgrupables.forEach((mov: any) => {
      const tipo = (mov.tipo || '').toLowerCase();
      const monto = Number(mov.monto) || 0;
      const fecha = new Date(mov.fecha);
      const dia = fecha.getDate().toString().padStart(2, '0');
      const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
      const anio = fecha.getFullYear();

      let debito = 0;
      let credito = 0;

      if (['ingreso', 'contado', 'enganche', 'abono'].includes(tipo)) {
        credito = monto;
        saldoAcumulado += monto;
      } else if (['egreso', 'comision'].includes(tipo)) {
        debito = monto;
        saldoAcumulado -= monto;
      }

      const noAutorizacion = extraerNoAutorizacion(mov);
      const noDoc = (mov.numeroDocumento && mov.numeroDocumento.toString().trim() && mov.numeroDocumento.toString().trim() !== '-')
        ? mov.numeroDocumento.toString().trim()
        : (mov.referencia ? (() => {
          const noDocMatch = mov.referencia.match(/No\.\s*Doc\.?:\s*([^,]+)/i);
          return (noDocMatch && noDocMatch[1].trim() && noDocMatch[1].trim() !== '-') ? noDocMatch[1].trim() : '-';
        })() : '-');
      const documento = formatearDocumento(mov);

      filas.push({
        dia: `${dia}-${mes}-${anio}`,
        noAutorizacion,
        noDoc,
        documento,
        descripcion: mov.concepto || mov.descripcion || '-',
        debito,
        credito,
        saldo: saldoAcumulado
      });
    });

    // Ordenar por fecha y luego por orden de creación
    filas.sort((a, b) => {
      const fechaA = a.dia.split('-').reverse().join('-'); // DD-MM-YYYY -> YYYY-MM-DD
      const fechaB = b.dia.split('-').reverse().join('-');
      if (fechaA !== fechaB) {
        return fechaA.localeCompare(fechaB);
      }
      // Si misma fecha, mantener orden original (los consolidados primero)
      return 0;
    });

    return filas;
  }, [movimientosMes, saldoAnterior]);

  // Calcular totales
  const totales = useMemo(() => {
    const totalDebito = datosTabla.reduce((sum, fila) => sum + fila.debito, 0);
    const totalCredito = datosTabla.reduce((sum, fila) => sum + fila.credito, 0);
    const saldoFinal = datosTabla.length > 0 ? datosTabla[datosTabla.length - 1].saldo : saldoAnterior;

    return { totalDebito, totalCredito, saldoFinal };
  }, [datosTabla, saldoAnterior]);

  // Parsear PDF del banco y detectar mes/año automáticamente
  const parsearPDFBanco = async (file: File): Promise<{ movimientos: MovimientoBanco[], mesDetectado: number, anioDetectado: number }> => {
    try {
      console.log('=== INICIANDO PARSEO DE PDF ===');
      console.log('Archivo:', file.name, 'Tamaño:', file.size, 'Tipo:', file.type);

      console.log('Leyendo archivo PDF...');
      const arrayBuffer = await file.arrayBuffer();
      console.log('PDF cargado, tamaño:', arrayBuffer.byteLength, 'bytes');

      console.log('Worker configurado:', pdfjsLib.GlobalWorkerOptions.workerSrc);
      console.log('Inicializando PDF.js...');

      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        verbosity: 0 // Reducir logs de pdf.js
      });

      const pdf = await loadingTask.promise;
      console.log('✅ PDF inicializado exitosamente');
      console.log('Número de páginas:', pdf.numPages);

      const movimientos: MovimientoBanco[] = [];
      const mesesEncontrados = new Set<number>();
      const aniosEncontrados = new Set<number>();

      // Procesar todas las páginas
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();

        // Obtener texto con posiciones para mejor parsing
        const textItems = textContent.items.map((item: any) => ({
          str: item.str,
          x: item.transform?.[4] || 0,
          y: item.transform?.[5] || 0,
          width: item.width || 0,
          height: item.height || 0
        }));

        // Buscar período en el texto completo
        const textoCompleto = textItems.map((item: any) => item.str).join(' ');

        // Buscar período: "Comprendidos del 01/10/2025 al 31/10/2025" o "del 01/10/2025 al 31/10/2025"
        const periodoMatch = textoCompleto.match(/(?:Comprendidos|del)[\s]+(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})[\s]+(?:al|hasta)[\s]+(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})/i) ||
          textoCompleto.match(/(Enero|Febrero|Marzo|Abril|Mayo|Junio|Julio|Agosto|Septiembre|Octubre|Noviembre|Diciembre)[\s]+(\d{4})/i);

        if (periodoMatch) {
          if (periodoMatch[3] && periodoMatch[6]) {
            // Formato fecha completa: del DD/MM/YYYY al DD/MM/YYYY
            const mesInicio = parseInt(periodoMatch[2]) - 1;
            const anioInicio = parseInt(periodoMatch[3]);
            const mesFin = parseInt(periodoMatch[5]) - 1;
            const anioFin = parseInt(periodoMatch[6]);

            // Usar el mes de inicio (ambos deberían ser del mismo mes)
            mesesEncontrados.add(mesInicio);
            aniosEncontrados.add(anioInicio);
            console.log(`📅 Período detectado: ${mesInicio + 1}/${anioInicio} al ${mesFin + 1}/${anioFin}`);
          } else if (periodoMatch[1] && !periodoMatch[3]) {
            // Formato texto mes
            const mesesTexto: { [key: string]: number } = {
              'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3,
              'mayo': 4, 'junio': 5, 'julio': 6, 'agosto': 7,
              'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
            };
            const mesTexto = periodoMatch[1].toLowerCase();
            const mes = mesesTexto[mesTexto] ?? -1;
            const anio = parseInt(periodoMatch[2]);
            if (mes >= 0) {
              mesesEncontrados.add(mes);
              aniosEncontrados.add(anio);
              console.log(`📅 Mes detectado por texto: ${mesTexto} ${anio}`);
            }
          }
        }

        // Agrupar texto por líneas basándose en la posición Y
        const lineas: Array<Array<any>> = [];
        let lineaActual: Array<any> = [];
        let yActual = -1;
        const toleranciaY = 3; // Tolerancia para agrupar en la misma línea

        textItems.forEach((item: any) => {
          if (yActual === -1 || Math.abs(item.y - yActual) > toleranciaY) {
            if (lineaActual.length > 0) {
              lineas.push(lineaActual);
            }
            lineaActual = [item];
            yActual = item.y;
          } else {
            lineaActual.push(item);
          }
        });
        if (lineaActual.length > 0) {
          lineas.push(lineaActual);
        }

        // Ordenar líneas por posición Y (de arriba a abajo)
        lineas.sort((a, b) => (b[0]?.y || 0) - (a[0]?.y || 0));

        // Variable para rastrear el saldo anterior
        let saldoAnterior = 0;
        let primeraLineaConSaldo = true;

        // Procesar cada línea
        for (const lineaItems of lineas) {
          // Ordenar items de la línea por posición X (de izquierda a derecha)
          lineaItems.sort((a: any, b: any) => (a.x || 0) - (b.x || 0));

          const lineaTexto = lineaItems.map((item: any) => item.str).join(' ').trim();
          if (!lineaTexto || lineaTexto.length < 5) continue;

          // Buscar patrón de fecha (DD-MM-YYYY o DD/MM/YYYY) - puede estar en cualquier parte
          const fechaMatch = lineaTexto.match(/(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})/);
          if (!fechaMatch) continue;

          const [, dia, mes, anio] = fechaMatch;
          const mesNum = parseInt(mes) - 1; // Mes en formato 0-11
          const anioNum = parseInt(anio);

          // Guardar mes y año encontrados
          mesesEncontrados.add(mesNum);
          aniosEncontrados.add(anioNum);

          const fecha = `${dia.padStart(2, '0')}-${mes.padStart(2, '0')}-${anio}`;

          // Buscar montos usando regex más robusto
          // Formato puede ser: 1,234.56 o 1.234,56 o 1234.56 o 1,234,56.78
          const montoRegex = /(\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{2})?|\d+[.,]\d{2})/g;
          const montosEncontrados: Array<{ valor: number, texto: string, posicion: number }> = [];
          let match;
          let posicion = 0;

          while ((match = montoRegex.exec(lineaTexto)) !== null) {
            const montoTexto = match[0];
            // Limpiar y convertir: remover separadores de miles, mantener decimales
            let montoStr = montoTexto;

            // Si tiene punto y coma, determinar cuál es separador de miles
            if (montoTexto.includes('.') && montoTexto.includes(',')) {
              // El último es el separador decimal
              const ultimoPunto = montoTexto.lastIndexOf('.');
              const ultimaComa = montoTexto.lastIndexOf(',');
              if (ultimoPunto > ultimaComa) {
                // Punto es decimal: 1.234,56
                montoStr = montoTexto.replace(/\./g, '').replace(',', '.');
              } else {
                // Coma es decimal: 1,234.56
                montoStr = montoTexto.replace(/,/g, '');
              }
            } else if (montoTexto.includes(',')) {
              // Solo coma: puede ser 1,234 o 1234,56
              if (montoTexto.split(',')[1]?.length === 2) {
                // Es decimal: 1234,56
                montoStr = montoTexto.replace(',', '.');
              } else {
                // Es separador de miles: 1,234
                montoStr = montoTexto.replace(/,/g, '');
              }
            } else if (montoTexto.includes('.')) {
              // Solo punto: puede ser 1.234 o 1234.56
              if (montoTexto.split('.')[1]?.length === 2) {
                // Es decimal: 1234.56
                montoStr = montoTexto;
              } else {
                // Es separador de miles: 1.234
                montoStr = montoTexto.replace(/\./g, '');
              }
            }

            const monto = parseFloat(montoStr);
            if (!isNaN(monto) && monto > 0) {
              montosEncontrados.push({
                valor: monto,
                texto: montoTexto,
                posicion: match.index || 0
              });
              console.log(`💰 Monto encontrado: "${montoTexto}" -> ${montoStr} -> ${monto}`);
            }
            posicion++;
          }

          // Necesitamos al menos 1 monto (el saldo)
          if (montosEncontrados.length < 1) continue;

          // Ordenar montos por posición en el texto (de izquierda a derecha)
          montosEncontrados.sort((a, b) => a.posicion - b.posicion);

          // El último monto es el saldo
          const saldo = montosEncontrados[montosEncontrados.length - 1].valor;

          // Determinar débito y crédito basándose en la posición
          // En el formato del banco: fecha | documento | descripción | débito | crédito | saldo
          let debito = 0;
          let credito = 0;

          if (montosEncontrados.length >= 3) {
            // Hay débito, crédito y saldo
            debito = montosEncontrados[montosEncontrados.length - 3].valor;
            credito = montosEncontrados[montosEncontrados.length - 2].valor;
          } else if (montosEncontrados.length === 2) {
            // Hay un movimiento (débito o crédito) y saldo
            const montoMovimiento = montosEncontrados[0].valor;

            // Determinar si es débito o crédito comparando con saldo anterior
            if (primeraLineaConSaldo) {
              // Primera línea: no podemos determinar, asumimos crédito
              credito = montoMovimiento;
              primeraLineaConSaldo = false;
            } else {
              // Comparar saldo actual con saldo anterior
              const diferencia = saldo - saldoAnterior;
              if (Math.abs(diferencia - montoMovimiento) < 0.01) {
                // El monto coincide con la diferencia del saldo
                if (diferencia > 0) {
                  credito = montoMovimiento;
                } else {
                  debito = Math.abs(montoMovimiento);
                }
              } else {
                // No coincide exactamente, usar heurística
                if (saldo > saldoAnterior) {
                  credito = montoMovimiento;
                } else {
                  debito = montoMovimiento;
                }
              }
            }
          } else if (montosEncontrados.length === 1) {
            // Solo saldo, no hay movimiento nuevo en esta línea
            // Puede ser el saldo anterior
            if (primeraLineaConSaldo && lineaTexto.toLowerCase().includes('saldo anterior')) {
              saldoAnterior = saldo;
              primeraLineaConSaldo = false;
            }
            continue;
          }

          // Actualizar saldo anterior para la siguiente iteración
          saldoAnterior = saldo;

          // Extraer documento y descripción
          // Buscar el texto entre la fecha y los montos
          const indiceFecha = lineaTexto.indexOf(fechaMatch[0]);
          const textoDespuesFecha = lineaTexto.substring(indiceFecha + fechaMatch[0].length);

          // Dividir en partes para extraer documento y descripción
          const partes = textoDespuesFecha.trim().split(/\s+/);

          // El documento suele ser el primer elemento después de la fecha (número)
          let documento = '-';
          if (partes.length > 0 && partes[0].match(/^\d+$/)) {
            documento = partes[0];
          }

          // La descripción es el texto entre el documento y los montos
          const inicioDescripcion = documento !== '-' ? 1 : 0;
          const textoAntesMontos = textoDespuesFecha.substring(0, montosEncontrados[0]?.posicion ?
            montosEncontrados[0].posicion - indiceFecha - fechaMatch[0].length : textoDespuesFecha.length);
          const descripcion = textoAntesMontos.trim().replace(/^\d+\s+/, '').trim() || '-';

          // Solo agregar si tiene al menos un monto válido (débito o crédito)
          if (debito > 0 || credito > 0) {
            movimientos.push({
              fecha,
              documento: documento || '-',
              descripcion: descripcion || '-',
              debito,
              credito,
              saldo
            });
            console.log(`✅ Movimiento parseado: ${fecha} | Doc: ${documento} | Desc: ${descripcion.substring(0, 30)} | Débito: ${debito} | Crédito: ${credito} | Saldo: ${saldo}`);
          }
        }
      }

      // Determinar el mes y año más común en los movimientos
      const mesDetectado = mesesEncontrados.size > 0 ? Array.from(mesesEncontrados)[0] : selectedMonth;
      const anioDetectado = aniosEncontrados.size > 0 ? Array.from(aniosEncontrados)[0] : selectedYear;

      console.log('Movimientos extraídos:', movimientos.length);
      console.log('Mes detectado en PDF:', mesDetectado, '(', months[mesDetectado], ')');
      console.log('Año detectado en PDF:', anioDetectado);
      console.log('=== FIN DEL PARSEO ===');

      return { movimientos, mesDetectado, anioDetectado };
    } catch (error: any) {
      console.error('❌ Error al parsear PDF:', error);
      console.error('Detalles del error:', {
        message: error?.message,
        name: error?.name,
        stack: error?.stack
      });

      let mensajeError = 'Error al procesar el PDF del banco. ';
      if (error?.message?.includes('worker')) {
        mensajeError += 'Problema con el worker de PDF.js. ';
      } else if (error?.message?.includes('Invalid PDF')) {
        mensajeError += 'El archivo no es un PDF válido. ';
      } else {
        mensajeError += 'Asegúrate de que el archivo sea un PDF válido. ';
      }
      mensajeError += `Detalles: ${error?.message || 'Error desconocido'}`;

      throw new Error(mensajeError);
    }
  };

  // Comparar movimientos del sistema con los del banco
  const compararMovimientos = async () => {
    console.log('🚀 === INICIANDO COMPARACIÓN ===');
    console.log('📄 pdfFile:', pdfFile);
    console.log('✅ pdfFile existe?', !!pdfFile);

    if (!pdfFile) {
      console.error('❌ No hay archivo PDF seleccionado');
      toast.error('Por favor selecciona un archivo PDF primero');
      return;
    }

    console.log('⏳ Activando estado de procesamiento...');
    setProcesandoPDF(true);
    setEstadoProceso('Leyendo archivo PDF...');
    console.log('✅ Estado actualizado, procesandoPDF:', true);

    toast.info('Iniciando comparación...', { duration: 2000 });

    // Forzar re-render para mostrar el estado
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      console.log('Parseando archivo:', pdfFile.name, 'Tamaño:', pdfFile.size, 'Tipo:', pdfFile.type);

      // Determinar si es PDF o CSV
      const isPDF = pdfFile.type === 'application/pdf' || pdfFile.name.toLowerCase().endsWith('.pdf');
      const isCSV = pdfFile.type === 'text/csv' ||
        pdfFile.name.toLowerCase().endsWith('.csv') ||
        pdfFile.type === 'application/vnd.ms-excel';

      setEstadoProceso(isCSV ? 'Procesando CSV del banco...' : 'Procesando PDF del banco...');
      toast.info(isCSV ? 'Procesando CSV del banco...' : 'Procesando PDF del banco...', { duration: 3000 });

      // Parsear archivo del banco y detectar mes/año automáticamente
      const { movimientos: movimientosBanco, mesDetectado, anioDetectado } = isCSV
        ? await parsearCSVBanco(pdfFile)
        : await parsearPDFBanco(pdfFile);
      console.log('Movimientos del banco parseados:', movimientosBanco.length);
      console.log('Mes detectado en PDF:', mesDetectado, '(', months[mesDetectado], ')');
      console.log('Año detectado en PDF:', anioDetectado);

      // Verificar si el mes detectado coincide con el seleccionado
      if (mesDetectado !== selectedMonth || anioDetectado !== selectedYear) {
        const mensaje = `El PDF corresponde a ${months[mesDetectado]} ${anioDetectado}, pero estás viendo ${months[selectedMonth]} ${selectedYear}. ¿Deseas cambiar el período?`;
        toast.warning(mensaje, { duration: 6000 });
        console.warn('⚠️ Diferencia de período detectada');
      }

      // Obtener movimientos del sistema para el mes detectado en el PDF
      const movimientosSistemaMes = expenses
        .filter((mov: any) => {
          if (!mov.fecha) return false;
          const fechaMov = new Date(mov.fecha);
          return fechaMov.getMonth() === mesDetectado && fechaMov.getFullYear() === anioDetectado;
        })
        .sort((a: any, b: any) => {
          const fechaA = new Date(a.fecha).getTime();
          const fechaB = new Date(b.fecha).getTime();
          return fechaA - fechaB;
        });

      // Calcular saldo anterior para el mes detectado
      const saldoAnteriorDetectado = expenses
        .filter((mov: any) => {
          if (!mov.fecha) return false;
          const fechaMov = new Date(mov.fecha);
          const fechaLimite = new Date(anioDetectado, mesDetectado, 1);
          return fechaMov < fechaLimite;
        })
        .reduce((saldo: number, mov: any) => {
          const tipo = (mov.tipo || '').toLowerCase();
          const monto = Number(mov.monto) || 0;

          if (['ingreso', 'contado', 'enganche', 'abono'].includes(tipo)) {
            return saldo + monto;
          } else if (['egreso', 'comision'].includes(tipo)) {
            return saldo - monto;
          }
          return saldo;
        }, 0);

      // Agrupar movimientos del sistema por numeroDocumento y fecha antes de procesar
      // (igual que en datosTabla para mantener consistencia)
      const gruposSistema = new Map<string, any[]>();
      const movimientosSistemaNoAgrupables: any[] = [];

      movimientosSistemaMes.forEach((mov: any) => {
        const noDoc = (mov.numeroDocumento && mov.numeroDocumento.toString().trim() && mov.numeroDocumento.toString().trim() !== '-')
          ? mov.numeroDocumento.toString().trim()
          : (mov.referencia ? (() => {
            const noDocMatch = mov.referencia.match(/No\.\s*Doc\.?:\s*([^,]+)/i);
            return (noDocMatch && noDocMatch[1].trim() && noDocMatch[1].trim() !== '-') ? noDocMatch[1].trim() : null;
          })() : null);

        const fecha = new Date(mov.fecha);
        const fechaStr = fecha.toISOString().split('T')[0];

        const tipo = (mov.tipo || '').toLowerCase();
        const esIngreso = ['ingreso', 'contado', 'enganche', 'abono'].includes(tipo);

        if (noDoc && noDoc !== '-' && esIngreso) {
          const claveGrupo = `${noDoc}_${fechaStr}`;
          if (!gruposSistema.has(claveGrupo)) {
            gruposSistema.set(claveGrupo, []);
          }
          gruposSistema.get(claveGrupo)!.push(mov);
        } else {
          movimientosSistemaNoAgrupables.push(mov);
        }
      });

      // Preparar movimientos del sistema en formato similar (agrupados)
      let saldoAcumulado = saldoAnteriorDetectado;
      const movimientosSistema: MovimientoBanco[] = [];

      // Procesar grupos consolidados
      gruposSistema.forEach((movimientosGrupo, claveGrupo) => {
        if (movimientosGrupo.length === 1) {
          // Solo un movimiento, agregar a no agrupables
          movimientosSistemaNoAgrupables.push(movimientosGrupo[0]);
        } else {
          // Múltiples movimientos con mismo numeroDocumento y fecha - consolidar
          const primerMov = movimientosGrupo[0];
          const fecha = new Date(primerMov.fecha);
          const dia = fecha.getDate().toString().padStart(2, '0');
          const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
          const anio = fecha.getFullYear();

          // Sumar montos
          const montoTotal = movimientosGrupo.reduce((sum, m) => sum + (Number(m.monto) || 0), 0);

          // Construir descripción consolidada
          const lotesUnicos = new Set<string>();
          const compradoresUnicos = new Set<string>();

          movimientosGrupo.forEach((m: any) => {
            if (m.lotes) {
              const lotesArray = Array.isArray(m.lotes) ? m.lotes : [m.lotes];
              lotesArray.forEach((l: string) => lotesUnicos.add(l));
            }
            if (m.comprador) {
              compradoresUnicos.add(m.comprador);
            } else if (m.concepto) {
              const matchComprador = m.concepto.match(/(?:Abono manual de|Abono de|Enganche por venta de lotes.*a)\s+([^para]+?)(?:\s+para|\s+a|$)/i);
              if (matchComprador) {
                compradoresUnicos.add(matchComprador[1].trim());
              }
            }
          });

          const lotesStr = Array.from(lotesUnicos).sort().join(', ');
          const compradoresStr = Array.from(compradoresUnicos).join(', ');
          const descripcionConsolidada = `Abono manual de ${compradoresStr || 'varios compradores'} para lotes ${lotesStr}`;

          const noDoc = (primerMov.numeroDocumento && primerMov.numeroDocumento.toString().trim() && primerMov.numeroDocumento.toString().trim() !== '-')
            ? primerMov.numeroDocumento.toString().trim()
            : (primerMov.referencia ? (() => {
              const noDocMatch = primerMov.referencia.match(/No\.\s*Doc\.?:\s*([^,]+)/i);
              return (noDocMatch && noDocMatch[1].trim() && noDocMatch[1].trim() !== '-') ? noDocMatch[1].trim() : '-';
            })() : '-');

          movimientosSistema.push({
            fecha: `${dia}-${mes}-${anio}`,
            documento: formatearDocumento(primerMov),
            transferencia: extraerTransferencia(primerMov),
            noAutorizacion: extraerNoAutorizacion(primerMov),
            noDoc: noDoc,
            descripcion: descripcionConsolidada,
            debito: 0,
            credito: montoTotal,
            saldo: saldoAcumulado + montoTotal
          });

          saldoAcumulado += montoTotal;
        }
      });

      // Procesar movimientos no agrupables
      movimientosSistemaNoAgrupables.forEach((mov: any) => {
        const tipo = (mov.tipo || '').toLowerCase();
        const monto = Number(mov.monto) || 0;
        const fecha = new Date(mov.fecha);
        const dia = fecha.getDate().toString().padStart(2, '0');
        const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
        const anio = fecha.getFullYear();

        let debito = 0;
        let credito = 0;

        if (['ingreso', 'contado', 'enganche', 'abono'].includes(tipo)) {
          credito = monto;
          saldoAcumulado += monto;
        } else if (['egreso', 'comision'].includes(tipo)) {
          debito = monto;
          saldoAcumulado -= monto;
        }

        const transferencia = extraerTransferencia(mov);
        const documento = formatearDocumento(mov);
        const noAutorizacion = extraerNoAutorizacion(mov);
        const noDoc = (mov.numeroDocumento && mov.numeroDocumento.toString().trim() && mov.numeroDocumento.toString().trim() !== '-')
          ? mov.numeroDocumento.toString().trim()
          : (mov.referencia ? (() => {
            const noDocMatch = mov.referencia.match(/No\.\s*Doc\.?:\s*([^,]+)/i);
            return (noDocMatch && noDocMatch[1].trim() && noDocMatch[1].trim() !== '-') ? noDocMatch[1].trim() : '-';
          })() : '-');

        movimientosSistema.push({
          fecha: `${dia}-${mes}-${anio}`,
          documento: documento,
          transferencia: transferencia,
          noAutorizacion: noAutorizacion,
          noDoc: noDoc,
          descripcion: mov.concepto || mov.descripcion || '-',
          debito,
          credito,
          saldo: saldoAcumulado
        });
      });

      console.log('Movimientos del sistema para', months[mesDetectado], anioDetectado, ':', movimientosSistema.length);

      setEstadoProceso('Comparando movimientos...');
      toast.info('Comparando movimientos del banco con el sistema...', { duration: 3000 });

      // Comparar movimientos
      const resultado: ComparacionResultado = {
        movimientosSistema,
        movimientosBanco,
        faltantesEnSistema: [],
        faltantesEnBanco: [],
        coincidencias: [],
        diferencias: []
      };

      // Crear mapas para comparación rápida
      // Comparación por: No. Doc. (Sistema) = No. Doc. (Banco) + Monto (Debe o Haber)
      // IMPORTANTE: El No. Doc. del sistema debe hacer match con el No. Doc. del CSV del banco
      const sistemaMap = new Map<string, MovimientoBanco>();
      movimientosSistema.forEach(mov => {
        const monto = mov.debito > 0 ? mov.debito : mov.credito;
        // Usar noDoc para comparar (es el No. Doc. del banco)
        const noDocSistema = (mov.noDoc || mov.transferencia || mov.documento || '').toString().trim().toUpperCase();
        const key = `${noDocSistema}-${monto.toFixed(2)}`;
        sistemaMap.set(key, mov);
        console.log(`📋 Sistema: No. Doc.="${noDocSistema}" | No. Autorización="${mov.noAutorizacion || '-'}" | Doc="${mov.documento}" | Monto=${monto.toFixed(2)}`);
      });

      const bancoMap = new Map<string, MovimientoBanco>();
      movimientosBanco.forEach(mov => {
        const monto = mov.debito > 0 ? mov.debito : mov.credito;
        // El documento del banco es el No. Doc. del CSV
        const noDocBanco = (mov.noDoc || mov.documento || '').toString().trim().toUpperCase();
        const key = `${noDocBanco}-${monto.toFixed(2)}`;
        bancoMap.set(key, mov);
        console.log(`🏦 Banco: No. Doc.="${noDocBanco}" | Desc="${mov.descripcion.substring(0, 30)}" | Monto=${monto.toFixed(2)}`);
      });

      console.log('🔍 Comparación por: No. Doc. (Sistema) = No. Doc. (Banco) + Monto');
      console.log('📊 Movimientos del sistema:', movimientosSistema.length);
      console.log('📊 Movimientos del banco:', movimientosBanco.length);

      // Encontrar coincidencias y diferencias
      movimientosSistema.forEach(movSistema => {
        const monto = movSistema.debito > 0 ? movSistema.debito : movSistema.credito;
        // Usar noDoc para buscar en el banco
        const noDocSistema = (movSistema.noDoc || movSistema.transferencia || movSistema.documento || '').toString().trim().toUpperCase();
        const key = `${noDocSistema}-${monto.toFixed(2)}`;
        const movBanco = bancoMap.get(key);

        if (movBanco) {
          // Verificar si coinciden completamente (documento y monto ya coinciden por la key)
          const tolerancia = 0.01;
          const montoSistema = movSistema.debito > 0 ? movSistema.debito : movSistema.credito;
          const montoBanco = movBanco.debito > 0 ? movBanco.debito : movBanco.credito;

          if (Math.abs(montoSistema - montoBanco) < tolerancia) {
            resultado.coincidencias.push({ sistema: movSistema, banco: movBanco });
            console.log(`✅ Coincidencia: No. Doc. Sistema "${noDocSistema}" = No. Doc. Banco "${movBanco.noDoc || movBanco.documento}" | Monto ${monto.toFixed(2)}`);
          } else {
            resultado.diferencias.push({
              sistema: movSistema,
              banco: movBanco,
              motivo: `Diferencia en monto: Sistema ${montoSistema.toFixed(2)} vs Banco ${montoBanco.toFixed(2)}`
            });
          }
        } else {
          resultado.faltantesEnBanco.push(movSistema);
          console.log(`⚠️ Faltante en banco: No. Doc. "${noDocSistema}" | No. Autorización "${movSistema.noAutorizacion || '-'}" | Monto ${monto.toFixed(2)} | Desc: ${movSistema.descripcion.substring(0, 40)}`);
        }
      });

      // Encontrar movimientos en el banco que no están en el sistema
      movimientosBanco.forEach(movBanco => {
        const monto = movBanco.debito > 0 ? movBanco.debito : movBanco.credito;
        // El documento del banco es el No. Doc. del CSV
        const noDocBanco = (movBanco.noDoc || movBanco.documento || '').toString().trim().toUpperCase();
        const key = `${noDocBanco}-${monto.toFixed(2)}`;
        if (!sistemaMap.has(key)) {
          resultado.faltantesEnSistema.push(movBanco);
          console.log(`⚠️ Faltante en sistema: No. Doc. "${noDocBanco}" | Monto ${monto.toFixed(2)} | Desc: ${movBanco.descripcion.substring(0, 40)}`);
        }
      });

      setEstadoProceso('Finalizando comparación...');
      setComparacion(resultado);
      setMesComparacion(mesDetectado);
      setAnioComparacion(anioDetectado);
      setEstadoProceso('');

      if (resultado.faltantesEnSistema.length === 0 &&
        resultado.faltantesEnBanco.length === 0 &&
        resultado.diferencias.length === 0) {
        toast.success('✅ Los estados de cuenta coinciden perfectamente');
      } else {
        toast.warning(`⚠️ Se encontraron ${resultado.faltantesEnSistema.length + resultado.faltantesEnBanco.length + resultado.diferencias.length} discrepancias`);
      }
    } catch (error: any) {
      console.error('❌ Error al comparar:', error);
      console.error('Stack:', error?.stack);
      const mensajeError = error?.message || 'Error al comparar los estados de cuenta';
      toast.error(mensajeError, {
        duration: 5000,
        description: 'Revisa la consola para más detalles'
      });
      setEstadoProceso('Error al procesar');
    } finally {
      console.log('🏁 Finalizando proceso...');
      setProcesandoPDF(false);
      setEstadoProceso('');
      console.log('✅ Estado limpiado, procesandoPDF:', false);
      console.log('🏁 === FIN DE COMPARACIÓN ===');
    }
  };

  // Parsear CSV del banco
  // Formato esperado: Fecha, TT, Descripción, No. Doc., Debe, Haber, Saldo
  const parsearCSVBanco = async (file: File): Promise<{ movimientos: MovimientoBanco[], mesDetectado: number, anioDetectado: number }> => {
    try {
      console.log('=== INICIANDO PARSEO DE CSV ===');
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim().length > 0);

      const movimientos: MovimientoBanco[] = [];
      const mesesEncontrados = new Set<number>();
      const aniosEncontrados = new Set<number>();

      // Buscar encabezados - formato: Fecha, TT, Descripción, No. Doc., Debe, Haber, Saldo
      let headerIndex = -1;
      let columnIndices: { fecha: number, tt: number, descripcion: number, documento: number, debe: number, haber: number, saldo: number } | null = null;

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].toLowerCase();
        // Buscar encabezado con las columnas esperadas
        if (line.includes('fecha') && (line.includes('no. doc') || line.includes('no doc') || line.includes('documento')) &&
          line.includes('debe') && line.includes('haber')) {
          headerIndex = i;

          // Parsear encabezado para encontrar índices de columnas
          const headerColumns = lines[i].split(/[,;]/).map(col => col.trim().toLowerCase());

          columnIndices = {
            fecha: headerColumns.findIndex(col => col.includes('fecha') || col.includes('día')),
            tt: headerColumns.findIndex(col => col.includes('tt') || col === 'tt'),
            descripcion: headerColumns.findIndex(col => col.includes('descripción') || col.includes('descripcion') || col.includes('concepto')),
            documento: headerColumns.findIndex(col => col.includes('no. doc') || col.includes('no doc') || col.includes('documento') || col.includes('doc')),
            debe: headerColumns.findIndex(col => col.includes('debe') || col.includes('débito') || col.includes('debito')),
            haber: headerColumns.findIndex(col => col.includes('haber') || col.includes('crédito') || col.includes('credito')),
            saldo: headerColumns.findIndex(col => col.includes('saldo'))
          };

          console.log('📋 Encabezado encontrado en línea', i + 1);
          console.log('📋 Índices de columnas:', columnIndices);
          break;
        }
      }

      if (!columnIndices) {
        throw new Error('No se encontró el encabezado esperado. El CSV debe tener las columnas: Fecha, TT, Descripción, No. Doc., Debe, Haber, Saldo');
      }

      // Procesar líneas después del encabezado
      const startIndex = headerIndex >= 0 ? headerIndex + 1 : 0;

      // Función para parsear montos
      const parsearMonto = (montoStr: string): number => {
        if (!montoStr || montoStr.trim() === '' || montoStr === '-') return 0;

        let limpio = montoStr.trim().replace(/[^\d.,-]/g, '');

        if (limpio.includes('.') && limpio.includes(',')) {
          const ultimoPunto = limpio.lastIndexOf('.');
          const ultimaComa = limpio.lastIndexOf(',');
          if (ultimoPunto > ultimaComa) {
            limpio = limpio.replace(/\./g, '').replace(',', '.');
          } else {
            limpio = limpio.replace(/,/g, '');
          }
        } else if (limpio.includes(',')) {
          const partes = limpio.split(',');
          if (partes[1]?.length === 2) {
            limpio = limpio.replace(',', '.');
          } else {
            limpio = limpio.replace(/,/g, '');
          }
        } else if (limpio.includes('.')) {
          const partes = limpio.split('.');
          if (partes[1]?.length === 2 && partes.length === 2) {
            limpio = limpio;
          } else {
            limpio = limpio.replace(/\./g, '');
          }
        }

        const valor = parseFloat(limpio);
        return isNaN(valor) ? 0 : valor;
      };

      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line || line.toLowerCase().includes('total') || line.toLowerCase().includes('saldo anterior')) {
          continue;
        }

        // Dividir por comas o punto y coma, pero manejar comas dentro de campos entre comillas
        let columns: string[] = [];
        let currentColumn = '';
        let insideQuotes = false;

        for (let j = 0; j < line.length; j++) {
          const char = line[j];
          if (char === '"') {
            insideQuotes = !insideQuotes;
            currentColumn += char;
          } else if ((char === ',' || char === ';') && !insideQuotes) {
            columns.push(currentColumn.trim());
            currentColumn = '';
          } else {
            currentColumn += char;
          }
        }
        columns.push(currentColumn.trim()); // Agregar última columna

        // Si no hay comillas, usar split simple
        if (!line.includes('"')) {
          columns = line.split(/[,;]/).map(col => col.trim());
        }

        // Remover comillas de los valores
        columns = columns.map(col => col.replace(/^"|"$/g, '').trim());

        if (columns.length < 5) continue;

        // Extraer fecha (columna Fecha)
        const fechaStr = columns[columnIndices.fecha] || '';
        const fechaMatch = fechaStr.match(/(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})/);
        if (!fechaMatch) continue;

        const [, dia, mes, anio] = fechaMatch;
        const mesNum = parseInt(mes) - 1;
        const anioNum = parseInt(anio);

        mesesEncontrados.add(mesNum);
        aniosEncontrados.add(anioNum);

        const fecha = `${dia.padStart(2, '0')}-${mes.padStart(2, '0')}-${anio}`;

        // Extraer datos según índices de columnas
        const tt = columns[columnIndices.tt] || '-';
        const descripcion = columns[columnIndices.descripcion] || '-';
        const documento = columns[columnIndices.documento] || '-';
        const debeStr = columns[columnIndices.debe] || '0';
        const haberStr = columns[columnIndices.haber] || '0';
        const saldoStr = columns[columnIndices.saldo] || '0';

        const debito = parsearMonto(debeStr);
        const credito = parsearMonto(haberStr);
        const saldo = parsearMonto(saldoStr);

        // Solo agregar si tiene movimiento (debe o haber)
        if (debito > 0 || credito > 0) {
          const noDocBanco = documento.toString().trim();
          movimientos.push({
            fecha,
            documento: noDocBanco, // El documento del banco es el No. Doc.
            noDoc: noDocBanco, // Asignar también a noDoc para consistencia
            descripcion: descripcion.trim(),
            debito,
            credito,
            saldo
          });

          console.log(`✅ Movimiento CSV: ${fecha} | No. Doc.: ${noDocBanco} | Desc: ${descripcion.substring(0, 30)} | Debe: ${debito} | Haber: ${credito} | Saldo: ${saldo}`);
        }
      }

      const mesDetectado = mesesEncontrados.size > 0 ? Array.from(mesesEncontrados)[0] : selectedMonth;
      const anioDetectado = aniosEncontrados.size > 0 ? Array.from(aniosEncontrados)[0] : selectedYear;

      console.log(`✅ CSV parseado: ${movimientos.length} movimientos encontrados`);
      console.log(`📅 Mes detectado: ${months[mesDetectado]} ${anioDetectado}`);
      return { movimientos, mesDetectado, anioDetectado };
    } catch (error: any) {
      console.error('❌ Error al parsear CSV:', error);
      throw new Error(`Error al procesar el CSV: ${error.message}`);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const isCSV = file.type === 'text/csv' ||
        file.name.toLowerCase().endsWith('.csv') ||
        file.type === 'application/vnd.ms-excel';

      if (!isPDF && !isCSV) {
        toast.error('Por favor selecciona un archivo PDF o CSV válido');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      console.log('Archivo seleccionado:', file.name, 'Tipo:', file.type);
      setPdfFile(file);
      setComparacion(null);
      setMesComparacion(null);
      setAnioComparacion(null);
      toast.success(`${isPDF ? 'PDF' : 'CSV'} cargado: ${file.name}`);
    }
  };

  // Generar PDF del estado de cuenta
  const generarPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    let yPosition = 20;

    // ============ ENCABEZADO ============
    // Banner azul oscuro
    doc.setFillColor(0, 51, 102); // Azul oscuro similar al banco
    doc.rect(0, 0, pageWidth, 25, 'F');

    // Texto "Consulta personalizada - Estado de Cuenta"
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Consulta personalizada - Estado de Cuenta', pageWidth / 2, 15, { align: 'center' });

    yPosition = 35;

    // Logo y nombre del banco
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('BI BANCO INDUSTRIAL', pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 10;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(config?.nombreBanco || 'BANCO INDUSTRIAL, S.A', pageWidth / 2, yPosition, { align: 'center' });
    doc.text(config?.direccionBanco || '7a. Avenida 5-10 zona 4, Guatemala 01004 C.A.', pageWidth / 2, yPosition + 5, { align: 'center' });
    doc.text(`PBX: ${config?.telefonoBanco || '2420-3000'}`, pageWidth / 2, yPosition + 10, { align: 'center' });

    yPosition += 25;

    // Información de la cuenta
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Titular:', 20, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(config?.razonSocial || 'MILTIERRAS, SOCIEDAD ANONIMA', 50, yPosition);

    yPosition += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Dirección:', 20, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(config?.direccionCompleta || 'ZONA 0, ALDEA CHICHAIC, COBAN, ALTA VERAPAZ', 50, yPosition);

    yPosition += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Tipo:', 20, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(config?.tipoCuenta || 'Monetaria', 50, yPosition);

    yPosition += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Banco:', 20, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(config?.nombreBanco || 'Banco Industrial', 50, yPosition);

    yPosition += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Cuenta:', 20, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(config?.numeroCuenta || '1480128865', 50, yPosition);

    yPosition += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Período:', 20, yPosition);
    doc.setFont('helvetica', 'normal');
    const fechaInicio = `01/${String(selectedMonth + 1).padStart(2, '0')}/${selectedYear}`;
    const fechaFin = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const fechaFinStr = `${String(fechaFin).padStart(2, '0')}/${String(selectedMonth + 1).padStart(2, '0')}/${selectedYear}`;
    doc.text(`del ${fechaInicio} al ${fechaFinStr}`, 50, yPosition);

    yPosition += 15;

    // ============ TABLA DE MOVIMIENTOS ============
    const tableData: any[] = [];

    // Saldo anterior
    tableData.push({
      dia: '',
      noAutorizacion: '',
      noDoc: '',
      documento: '',
      descripcion: '****SALDO ANTERIOR****',
      debito: '',
      credito: '',
      saldo: saldoAnterior.toLocaleString('es-GT', { minimumFractionDigits: 2 })
    });

    // Movimientos del mes
    datosTabla.forEach((fila) => {
      tableData.push({
        dia: fila.dia,
        noAutorizacion: fila.noAutorizacion,
        noDoc: fila.noDoc,
        documento: fila.documento,
        descripcion: fila.descripcion,
        debito: fila.debito > 0 ? fila.debito.toLocaleString('es-GT', { minimumFractionDigits: 2 }) : '',
        credito: fila.credito > 0 ? fila.credito.toLocaleString('es-GT', { minimumFractionDigits: 2 }) : '',
        saldo: fila.saldo.toLocaleString('es-GT', { minimumFractionDigits: 2 })
      });
    });

    // Totales
    tableData.push({
      dia: '',
      noAutorizacion: '',
      noDoc: '',
      documento: '',
      descripcion: 'Totales:',
      debito: totales.totalDebito.toLocaleString('es-GT', { minimumFractionDigits: 2 }),
      credito: totales.totalCredito.toLocaleString('es-GT', { minimumFractionDigits: 2 }),
      saldo: totales.saldoFinal.toLocaleString('es-GT', { minimumFractionDigits: 2 })
    });

    autoTable(doc, {
      startY: yPosition,
      head: [['Día', 'No. Autorización', 'No. Doc.', 'Docto.', 'Descripción', 'Débito(GTQ.)', 'Crédito(GTQ.)', 'Saldo']],
      body: tableData.map(row => [
        row.dia,
        row.noAutorizacion,
        row.noDoc,
        row.documento,
        row.descripcion,
        row.debito,
        row.credito,
        row.saldo
      ]),
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [0, 51, 102],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 20 }, // Día
        1: { cellWidth: 25 }, // No. Autorización
        2: { cellWidth: 25 }, // No. Doc.
        3: { cellWidth: 25 }, // Docto
        4: { cellWidth: 50 }, // Descripción
        5: { cellWidth: 25, halign: 'right' }, // Débito
        6: { cellWidth: 25, halign: 'right' }, // Crédito
        7: { cellWidth: 25, halign: 'right' }, // Saldo
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
    });

    // ============ FOOTER ============
    const finalY = (doc as any).lastAutoTable.finalY || yPosition + 100;
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(
      'Nota: Revise sus operaciones dentro de los 30 días siguientes. Comunique por escrito cualquier cambio de dirección. ' +
      'Conserve este estado de cuenta, el banco no emite duplicados.',
      20,
      finalY + 10,
      { maxWidth: pageWidth - 40, align: 'left' }
    );

    // Guardar PDF
    const nombreArchivo = `Estado_Cuenta_${months[selectedMonth]}_${selectedYear}.pdf`;
    doc.save(nombreArchivo);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">Estado de Cuenta Bancario</h2>
          <p className="text-muted-foreground mt-1 text-[13px]">Genera estados de cuenta con formato bancario mostrando ingresos, egresos y saldo acumulado</p>
        </div>
      </div>

      {/* Filtros en Card */}
      <Card className="bg-card border border-border shadow-none rounded-lg overflow-hidden">
        <CardHeader className="pb-3 pt-4 px-4 border-b border-white/5">
          <CardTitle className="text-sm font-medium tracking-tight flex items-center gap-2 text-foreground">
            <Calendar className="h-4 w-4 text-gray-400" />
            Período de Consulta
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="mes" className="text-foreground">Mes</Label>
              <Select
                value={selectedMonth.toString()}
                onValueChange={(value) => setSelectedMonth(parseInt(value))}
              >
                <SelectTrigger id="mes" className="bg-secondary text-foreground rounded-md border border-input focus:border-ring focus:ring-1 focus:ring-white/20 transition w-full h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-secondary border border-input">
                  {months.map((mes, index) => (
                    <SelectItem key={index} value={index.toString()}>
                      {mes}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="anio" className="text-foreground">Año</Label>
              <Select
                value={selectedYear.toString()}
                onValueChange={(value) => setSelectedYear(parseInt(value))}
              >
                <SelectTrigger id="anio" className="bg-secondary text-foreground rounded-md border border-input focus:border-ring focus:ring-1 focus:ring-white/20 transition w-full h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-secondary border border-input">
                  {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((anio) => (
                    <SelectItem key={anio} value={anio.toString()}>
                      {anio}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumen en Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-card border border-border shadow-none rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Saldo Anterior</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              Q. {saldoAnterior.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border border-rose-500/20 shadow-none rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Débitos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-rose-500">
              Q. {totales.totalDebito.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border border-emerald-500/20 shadow-none rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Créditos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-500">
              Q. {totales.totalCredito.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de movimientos en Card */}
      <Card className="bg-card border border-border shadow-none rounded-lg overflow-hidden">
        <CardHeader className="pb-3 pt-4 px-4 border-b border-white/5">
          <CardTitle className="text-sm font-medium tracking-tight text-foreground">Registro de Movimientos ({datosTabla.length})</CardTitle>
          <CardDescription className="text-[13px] text-muted-foreground mt-1">
            Movimientos del mes de {months[selectedMonth]} {selectedYear}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-white/5">
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Día</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">No. Autorización</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">No. Doc.</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Documento</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Descripción</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10 text-right">Débito (GTQ.)</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10 text-right">Crédito (GTQ.)</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10 text-right">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="bg-white/[0.02] font-semibold border-b border-white/5">
                  <TableCell colSpan={5} className="px-3 py-3 text-[13px]">****SALDO ANTERIOR****</TableCell>
                  <TableCell className="px-3 py-3 text-right"></TableCell>
                  <TableCell className="px-3 py-3 text-right"></TableCell>
                  <TableCell className="px-3 py-3 text-right font-mono text-[13px] font-bold">
                    Q. {saldoAnterior.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                  </TableCell>
                </TableRow>
                {datosTabla.map((fila, index) => (
                  <TableRow key={index} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <TableCell className="px-3 py-3 text-[13px] font-medium">{fila.dia}</TableCell>
                    <TableCell className="px-3 py-3 font-mono text-[11px] text-muted-foreground">{fila.noAutorizacion}</TableCell>
                    <TableCell className="px-3 py-3 font-mono text-[11px] font-semibold">{fila.noDoc}</TableCell>
                    <TableCell className="px-3 py-3 font-mono text-[11px] text-muted-foreground">{fila.documento}</TableCell>
                    <TableCell className="px-3 py-3 text-[13px]">{fila.descripcion}</TableCell>
                    <TableCell className="px-3 py-3 text-right">
                      {fila.debito > 0 ? (
                        <span className="text-rose-500 font-mono text-[13px] font-medium">
                          Q. {fila.debito.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40">-</span>
                      )}
                    </TableCell>
                    <TableCell className="px-3 py-3 text-right">
                      {fila.credito > 0 ? (
                        <span className="text-emerald-500 font-mono text-[13px] font-medium">
                          Q. {fila.credito.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40">-</span>
                      )}
                    </TableCell>
                    <TableCell className="px-3 py-3 text-right font-mono text-[13px] font-medium">
                      Q. {fila.saldo.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-white/[0.02] font-bold border-t border-input">
                  <TableCell colSpan={5} className="px-3 py-3 text-[13px] font-bold">Totales:</TableCell>
                  <TableCell className="px-3 py-3 text-right text-rose-500 font-mono text-[13px] font-bold">
                    Q. {totales.totalDebito.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell className="px-3 py-3 text-right text-emerald-500 font-mono text-[13px] font-bold">
                    Q. {totales.totalCredito.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell className="px-3 py-3 text-right font-mono text-[13px] font-bold">
                    Q. {totales.saldoFinal.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Botón de descarga */}
      <div className="flex justify-end">
        <Button
          onClick={generarPDF}
          className="bg-transparent border border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10 rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3 flex items-center gap-1.5"
        >
          <Download className="h-3.5 w-3.5" />
          Descargar Estado de Cuenta PDF
        </Button>
      </div>

      {/* Comparación con PDF del banco */}
      <Card className="bg-card border border-border shadow-none rounded-lg overflow-hidden">
        <CardHeader className="pb-3 pt-4 px-4 border-b border-white/5">
          <CardTitle className="text-sm font-medium tracking-tight flex items-center gap-2 text-foreground">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Comparar con Estado de Cuenta del Banco
            {mesComparacion !== null && anioComparacion !== null && (
              <span className="text-[13px] font-normal text-muted-foreground ml-2">
                ({months[mesComparacion]} {anioComparacion})
              </span>
            )}
          </CardTitle>
          <CardDescription className="text-[13px] text-muted-foreground mt-1">
            Sube el PDF o CSV del estado de cuenta descargado del banco para compararlo con el generado por el sistema.
            El CSV es más fácil de procesar y más preciso.
            <br />
            <span className="text-[11px] text-muted-foreground/70 mt-1 block">
              <strong>Nota:</strong> La comparación se realiza usando el <strong>No. Doc.</strong> del sistema con el <strong>No. Doc.</strong> del CSV del banco, más el monto (Debe o Haber).
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf,.csv,text/csv"
                onChange={handleFileChange}
                className="hidden"
                id="pdf-banco-input"
              />
              <Button
                type="button"
                onClick={() => {
                  console.log('Click en seleccionar archivo');
                  fileInputRef.current?.click();
                }}
                className="bg-transparent border border-input hover:bg-white/5 text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3"
                disabled={procesandoPDF}
              >
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                {pdfFile ? 'Cambiar Archivo' : 'Seleccionar PDF/CSV del Banco'}
              </Button>
              {pdfFile ? (
                <div className="flex-1 flex items-center gap-2">
                  <p className="text-sm text-gray-600 font-medium">{pdfFile.name}</p>
                  <span className="text-xs text-green-600">✓ Cargado</span>
                </div>
              ) : (
                <div className="flex-1">
                  <p className="text-sm text-gray-400 italic">Ningún archivo seleccionado</p>
                </div>
              )}
            </div>

            {pdfFile && (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      console.log('🔵 CLICK EN COMPARAR');
                      console.log('🔵 pdfFile:', pdfFile);
                      console.log('🔵 procesandoPDF actual:', procesandoPDF);

                      if (procesandoPDF) {
                        console.log('⚠️ Ya está procesando, ignorando click');
                        toast.info('Ya hay un proceso en curso, por favor espera...');
                        return;
                      }

                      if (!pdfFile) {
                        console.error('❌ No hay pdfFile');
                        toast.error('No hay archivo PDF seleccionado');
                        return;
                      }

                      // Actualizar estado inmediatamente antes de ejecutar
                      console.log('⏳ Estableciendo procesandoPDF = true INMEDIATAMENTE');
                      setProcesandoPDF(true);
                      setEstadoProceso('Iniciando comparación...');

                      // Forzar re-render inmediato
                      setTimeout(() => {
                        console.log('✅ Ejecutando compararMovimientos después del timeout...');
                        compararMovimientos().catch((error) => {
                          console.error('❌ Error capturado en compararMovimientos:', error);
                          setProcesandoPDF(false);
                          setEstadoProceso('');
                        });
                      }, 50);
                    }}
                    disabled={procesandoPDF || !pdfFile}
                    className="bg-transparent border border-blue-500/50 text-blue-500 hover:bg-blue-500/10 rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed min-w-[200px]"
                  >
                    {procesandoPDF ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        Comparando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                        Comparar Estados de Cuenta
                      </>
                    )}
                  </Button>
                </div>

                {/* Indicador de progreso más visible */}
                {procesandoPDF && (
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-6 shadow-lg">
                    <div className="flex items-center gap-4">
                      <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                      <div className="flex-1">
                        <p className="text-base font-bold text-blue-900 mb-1">{estadoProceso || 'Procesando...'}</p>
                        <p className="text-sm text-blue-700">Por favor espera, esto puede tomar unos momentos</p>
                        <div className="mt-2 flex gap-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {comparacion && (
            <div className="mt-6 space-y-4">
              {/* Resumen de comparación */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-card border border-emerald-500/20 shadow-none rounded-lg">
                  <CardContent className="pt-4">
                    <div className="text-3xl font-bold text-emerald-500">
                      {comparacion.coincidencias.length}
                    </div>
                    <div className="text-xs text-muted-foreground">Coincidencias</div>
                  </CardContent>
                </Card>
                <Card className="bg-card border border-rose-500/20 shadow-none rounded-lg">
                  <CardContent className="pt-4">
                    <div className="text-3xl font-bold text-rose-500">
                      {comparacion.faltantesEnSistema.length}
                    </div>
                    <div className="text-xs text-muted-foreground">Faltantes en Sistema</div>
                  </CardContent>
                </Card>
                <Card className="bg-card border border-amber-500/20 shadow-none rounded-lg">
                  <CardContent className="pt-4">
                    <div className="text-3xl font-bold text-amber-500">
                      {comparacion.faltantesEnBanco.length}
                    </div>
                    <div className="text-xs text-muted-foreground">Faltantes en Banco</div>
                  </CardContent>
                </Card>
                <Card className="bg-card border border-yellow-500/20 shadow-none rounded-lg">
                  <CardContent className="pt-4">
                    <div className="text-3xl font-bold text-yellow-500">
                      {comparacion.diferencias.length}
                    </div>
                    <div className="text-xs text-muted-foreground">Diferencias</div>
                  </CardContent>
                </Card>
              </div>

              {/* Sección Principal: Estado de Cuenta del Banco (Referencia) */}
              {(() => {
                // Crear lista de movimientos del banco (coincidencias + faltantes en banco)
                interface MovimientoBancoRef {
                  tipo: 'coincidencia' | 'faltante-banco' | 'diferencia';
                  fecha: string;
                  noDoc: string;
                  noAutorizacion?: string;
                  documento: string;
                  descripcion: string;
                  debito: number;
                  credito: number;
                  saldo: number;
                  origen: 'banco' | 'sistema' | 'ambos';
                  motivo?: string;
                  movimientoSistema?: MovimientoBanco;
                  movimientoBanco?: MovimientoBanco;
                }

                const movimientosBancoRef: MovimientoBancoRef[] = [];

                // Agregar coincidencias (verde) - usar datos del banco como referencia
                comparacion.coincidencias.forEach((coincidencia) => {
                  movimientosBancoRef.push({
                    tipo: 'coincidencia',
                    fecha: coincidencia.banco.fecha,
                    noDoc: coincidencia.banco.noDoc || coincidencia.banco.documento,
                    documento: coincidencia.banco.documento,
                    descripcion: coincidencia.banco.descripcion || coincidencia.sistema.descripcion,
                    debito: coincidencia.banco.debito,
                    credito: coincidencia.banco.credito,
                    saldo: coincidencia.banco.saldo,
                    origen: 'ambos',
                  });
                });

                // Agregar faltantes en banco (naranja) - movimientos del sistema que no están en el banco
                comparacion.faltantesEnBanco.forEach((mov) => {
                  movimientosBancoRef.push({
                    tipo: 'faltante-banco',
                    fecha: mov.fecha,
                    noDoc: mov.noDoc && mov.noDoc !== '-' ? mov.noDoc : '-',
                    noAutorizacion: mov.noAutorizacion,
                    documento: mov.documento,
                    descripcion: mov.descripcion,
                    debito: mov.debito,
                    credito: mov.credito,
                    saldo: mov.saldo,
                    origen: 'sistema',
                  });
                });

                // Agregar diferencias (amarillo) - mostrar solo el del banco en esta sección
                comparacion.diferencias.forEach((diff) => {
                  movimientosBancoRef.push({
                    tipo: 'diferencia',
                    fecha: diff.banco.fecha,
                    noDoc: diff.banco.noDoc || diff.banco.documento,
                    documento: diff.banco.documento,
                    descripcion: diff.banco.descripcion,
                    debito: diff.banco.debito,
                    credito: diff.banco.credito,
                    saldo: diff.banco.saldo,
                    origen: 'banco',
                    motivo: diff.motivo,
                    movimientoBanco: diff.banco,
                    movimientoSistema: diff.sistema,
                  });
                });

                // Ordenar por fecha
                movimientosBancoRef.sort((a, b) => {
                  const fechaA = new Date(a.fecha.split('-').reverse().join('-')).getTime();
                  const fechaB = new Date(b.fecha.split('-').reverse().join('-')).getTime();
                  return fechaA - fechaB;
                });

                const getRowColor = (tipo: string) => {
                  switch (tipo) {
                    case 'coincidencia':
                      return 'bg-green-50 hover:bg-green-100';
                    case 'faltante-banco':
                      return 'bg-orange-50 hover:bg-orange-100';
                    case 'diferencia':
                      return 'bg-yellow-50 hover:bg-yellow-100';
                    default:
                      return 'bg-white';
                  }
                };

                const getTipoLabel = (tipo: string) => {
                  switch (tipo) {
                    case 'coincidencia':
                      return '✅ Coincidencia';
                    case 'faltante-banco':
                      return '⚠️ Faltante en Banco';
                    case 'diferencia':
                      return '🔄 Diferencia';
                    default:
                      return '';
                  }
                };

                return (
                  <div className="space-y-6">
                    {/* Sección Principal: Estado de Cuenta del Banco */}
                    <div>
                      <div className="mb-4">
                        <h3 className="text-xl font-bold text-gray-800 mb-2">
                          Estado de Cuenta del Banco - {mesComparacion !== null && anioComparacion !== null
                            ? `${months[mesComparacion]} ${anioComparacion}`
                            : `${months[selectedMonth]} ${selectedYear}`}
                        </h3>
                        <div className="flex gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Documentos en Banco:</span> {comparacion.movimientosBanco.length}
                          </div>
                          <div>
                            <span className="font-medium">Coincidencias:</span> {comparacion.coincidencias.length}
                          </div>
                          <div>
                            <span className="font-medium">Faltantes en Banco:</span> {comparacion.faltantesEnBanco.length}
                          </div>
                        </div>
                      </div>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50">
                              <TableHead className="w-16">#</TableHead>
                              <TableHead className="w-32">Estado</TableHead>
                              <TableHead>Fecha</TableHead>
                              <TableHead>No. Autorización</TableHead>
                              <TableHead>No. Doc.</TableHead>
                              <TableHead>Documento</TableHead>
                              <TableHead>Descripción</TableHead>
                              <TableHead className="text-right">Débito</TableHead>
                              <TableHead className="text-right">Crédito</TableHead>
                              <TableHead className="text-right">Saldo</TableHead>
                              <TableHead className="w-40">Motivo</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {movimientosBancoRef.map((mov, idx) => (
                              <TableRow key={idx} className={getRowColor(mov.tipo)}>
                                <TableCell className="font-semibold text-gray-600">{idx + 1}</TableCell>
                                <TableCell className="text-xs font-medium">
                                  {getTipoLabel(mov.tipo)}
                                </TableCell>
                                <TableCell>{mov.fecha}</TableCell>
                                <TableCell className="font-mono text-xs">
                                  {mov.noAutorizacion && mov.noAutorizacion !== '-' ? mov.noAutorizacion : '-'}
                                </TableCell>
                                <TableCell className="font-mono text-xs font-semibold">
                                  {mov.noDoc && mov.noDoc !== '-' ? mov.noDoc : '-'}
                                </TableCell>
                                <TableCell className="text-xs">{mov.documento}</TableCell>
                                <TableCell className="text-sm">{mov.descripcion}</TableCell>
                                <TableCell className="text-right font-medium text-red-600">
                                  {mov.debito > 0 ? `Q. ${mov.debito.toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : '-'}
                                </TableCell>
                                <TableCell className="text-right font-medium text-blue-600">
                                  {mov.credito > 0 ? `Q. ${mov.credito.toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : '-'}
                                </TableCell>
                                <TableCell className="text-right font-semibold">
                                  Q. {mov.saldo.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell className="text-xs text-gray-500">
                                  {mov.motivo || '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    {/* Sección Secundaria: Faltantes en Sistema (Referencia) */}
                    {comparacion.faltantesEnSistema.length > 0 && (
                      <div>
                        <div className="mb-4">
                          <h3 className="text-lg font-semibold text-gray-700 mb-2">
                            Movimientos Faltantes en el Sistema ({comparacion.faltantesEnSistema.length})
                          </h3>
                          <p className="text-sm text-gray-500">
                            Estos movimientos aparecen en el estado de cuenta del banco pero no están registrados en el sistema.
                            Se muestran como referencia para su registro.
                          </p>
                        </div>
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50">
                                <TableHead className="w-16">#</TableHead>
                                <TableHead>Fecha</TableHead>
                                <TableHead>No. Doc.</TableHead>
                                <TableHead>Documento</TableHead>
                                <TableHead>Descripción</TableHead>
                                <TableHead className="text-right">Débito</TableHead>
                                <TableHead className="text-right">Crédito</TableHead>
                                <TableHead className="text-right">Saldo</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {comparacion.faltantesEnSistema.map((mov, idx) => (
                                <TableRow key={idx} className="bg-red-50 hover:bg-red-100">
                                  <TableCell className="font-semibold text-gray-600">{idx + 1}</TableCell>
                                  <TableCell>{mov.fecha}</TableCell>
                                  <TableCell className="font-mono text-xs font-semibold">
                                    {(mov.noDoc && mov.noDoc !== '-') ? mov.noDoc : ((mov.documento && mov.documento !== '-') ? mov.documento : '-')}
                                  </TableCell>
                                  <TableCell className="text-xs">{mov.documento}</TableCell>
                                  <TableCell className="text-sm">{mov.descripcion}</TableCell>
                                  <TableCell className="text-right font-medium text-red-600">
                                    {mov.debito > 0 ? `Q. ${mov.debito.toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : '-'}
                                  </TableCell>
                                  <TableCell className="text-right font-medium text-blue-600">
                                    {mov.credito > 0 ? `Q. ${mov.credito.toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : '-'}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    Q. {mov.saldo.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Mensaje de éxito si todo coincide */}
              {comparacion.faltantesEnSistema.length === 0 &&
                comparacion.faltantesEnBanco.length === 0 &&
                comparacion.diferencias.length === 0 && (
                  <div className="bg-green-50 border border-gray-200 rounded-lg p-4 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-green-700 font-semibold">
                      ✅ Los estados de cuenta coinciden perfectamente. Todos los movimientos están registrados correctamente.
                    </span>
                  </div>
                )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de carga durante el procesamiento */}
      <Dialog open={procesandoPDF} onOpenChange={(open: boolean) => {
        // No permitir cerrar el modal mientras está procesando
        if (!open && procesandoPDF) {
          return;
        }
      }}>
        <DialogContent className="sm:max-w-md" onInteractOutside={(e: Event) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              Procesando Estado de Cuenta
            </DialogTitle>
            <DialogDescription>
              {estadoProceso || 'Comparando movimientos del banco con el sistema...'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {estadoProceso || 'Procesando...'}
                </p>
                <div className="flex gap-1 justify-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Por favor no cierres esta ventana
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

